import { PontoShapeModel } from "../../model/shape/ponto-shape-model";
import { Polygon, ILatLng, Marker, GoogleMap, GoogleMapsEvent, HtmlInfoWindow } from "@ionic-native/google-maps";
import * as turf from '@turf/turf';
import { Observable } from "rxjs/Observable";
import { Events } from "ionic-angular";
import { ShapeDrawOptions } from "./shape-draw-options";
import 'rxjs/add/observable/interval';
import { PolygonUtil } from "../../map/polygon-util";

export class PontosShapeListController {

	COLOR_SHAPE_INVISIBLE: string = 'rgba(102, 170, 170, 0)';
	COLOR_SHAPE_VISIBLE: string = 'rgba(102, 170, 170, 0.5)';

	private _points: PontoShapeModel[] = [];
	private _polygon: Polygon;
	private _middleMarkers: Marker[] = [];
	private _selectedPoint: PontoShapeModel;
	private areaShape: Number = 0;
	private _observable;

	constructor(private map: GoogleMap, private _events: Events) { }

	public addAll(positions: ILatLng[], options?:ShapeDrawOptions) {

		if (!options) {
			options = {
				index: null,
				drawMiddleMarkers: true
			};			
		}

		positions.forEach(p => {
			const marker = this.createMarkerPoint(p);
			const point = new PontoShapeModel(p, marker)
			this._points.push(point);
		});

		if (options.drawMiddleMarkers != false) {
			this.generateMiddleMarkers();
		}

		this.drawPolygon();
		this.unselectPoints();
	}

	public add(latLng: ILatLng, options?:ShapeDrawOptions): Marker {

		if (!options) {
			options = {
				index: null,
				drawMiddleMarkers: true
			};			
		}

		let marker = this.createMarkerPoint(latLng, options.index);
		const point = new PontoShapeModel(latLng, marker)

		if (options.index) {
			this._points.splice(options.index, 0, point);
		} else {
			this._points.push(point);
		}

		if (options.drawMiddleMarkers != false) {
			this.generateMiddleMarkers();
		}

		this.drawPolygon();
		this.unselectPoints();
		return marker;
	}

	public clear(): void {
        this._points.forEach(p => p.destroyMarker());
        this._middleMarkers.forEach(m => m.destroy());
        this._points = [];
		this._middleMarkers = [];
        this.areaShape = 0;
        this._polygon.destroy();
		//this.map.clear();
	}

	public selectMarker(marker: Marker): void {
		this.unselectMiddlePoints();
		let point = this._points[marker.get('_index')];
		this._points.forEach(p => p.unselectIfDiff(point));
		this._selectedPoint = point;
		point.setSelected(true);

		this._events.publish("shape-draw:deletePoint");
	}

	public getSelectedPoint(): PontoShapeModel {
		return this._selectedPoint;
	}

	public unselectPoints(): void {
		this.unselectMiddlePoints();
		this._points.forEach(p => p.setSelected(false));
		this._selectedPoint = null;
	}

	public removeSelectedPoint(): void {
		if (this._selectedPoint) {
			this._points.forEach((p, i, arr) => {
				if (p == this._selectedPoint) {
					p.destroyMarker();
					this._points.splice(i, 1);
				}
			});
			if (this._points.length != 0) {
				this.drawPolygon();
				this.generateMiddleMarkers();
				this.adjustMarkersIndexes();
				this.unselectPoints();
			}
		}
	}

	private createMarkerPoint(latLng: ILatLng, index?: number): Marker {
		let marker = this.map.addMarkerSync({
			icon: { url: './assets/imgs/marker-point.png' },
			position: latLng,
			draggable: true,
			disableAutoPan: true,
			'_index': index ? index : this.nextIndex()
		});
		marker.setAnimation(null);
		marker.setIconAnchor(16, 16);
		marker.on(GoogleMapsEvent.MARKER_CLICK).subscribe((params) => this.selectMarker(params[1]));
		marker.on(GoogleMapsEvent.MARKER_DRAG_START).subscribe(params => {
			this.selectMarker(params[1]);
			this.changeMarkerPosition(params[1]);
			this.clearMiddleMarkers();
			this._polygon.setFillColor(this.COLOR_SHAPE_INVISIBLE);
			this.observablePolygonArea("start");
		});
		marker.on(GoogleMapsEvent.MARKER_DRAG_END).subscribe(params => {
			this._polygon.setFillColor(this.COLOR_SHAPE_VISIBLE);
			this.generateMiddleMarkers();
			this.observablePolygonArea("end");
		});
		marker.on(GoogleMapsEvent.MARKER_DRAG).subscribe((params) => {
			this.changeMarkerPosition(params[1]);
		});
		return marker;
	}

	private clearMiddleMarkers() {
		this._middleMarkers.forEach(marker => marker.remove());
		this._middleMarkers.length = 0;
	}

	private generateMiddleMarkers() {
		this.clearMiddleMarkers();
		if (this._points.length < 3) {
			return;
		}
		for (let i = 0; i < this._points.length; i++) {
			let actualPoint = this._points[i];
			let nextPoint = this._points[i + 1];
			const finalMiddlePoint = nextPoint == null;
			if (finalMiddlePoint) {
				nextPoint = this._points[0];
			}

			const previousPointTurf = turf.point([nextPoint.latLng.lat, nextPoint.latLng.lng]);
			const actualPointTurf = turf.point([actualPoint.latLng.lat, actualPoint.latLng.lng]);

			const midPointTurf = turf.midpoint(previousPointTurf, actualPointTurf);
			const coordinates = midPointTurf.geometry.coordinates;

			const latLng = { lat: coordinates[0], lng: coordinates[1] };
			const middleMarker = this.createMiddleMarkerPoint(latLng, i, finalMiddlePoint ? 0 : i + 1);
			this._middleMarkers.push(middleMarker);
		}
	}

	private createMiddleMarkerPoint(latLng: ILatLng, indexBefore: number, indexAfter: number): Marker {
		let marker = this.map.addMarkerSync({
			icon: { url: './assets/imgs/marker-new.png' },
			position: latLng,
			draggable: false,
			disableAutoPan: true,
			'indexBefore': indexBefore,
			'indexAfter': indexAfter
		});
		marker.setIconAnchor(8, 8);
		marker.setOpacity(1);
		marker.setAnimation(null);
		marker.on(GoogleMapsEvent.MARKER_CLICK).subscribe((params) => {
			this.unselectMiddlePoints();
			this._points.forEach(p => p.unselectIfDiff(null));
			const marker: Marker = params[1];
			marker.setIcon({ url: './assets/imgs/marker-move.png' });
			const index = marker.get('indexAfter');
			marker.destroy();
			const newMarker = this.add(params[0], {
				index: index,
				drawMiddleMarkers: true
			});
			this.adjustMarkersIndexes();
			this.selectMarker(newMarker);
		});
		marker.on(GoogleMapsEvent.MARKER_DRAG_START).subscribe(params => this._polygon.setFillColor(this.COLOR_SHAPE_INVISIBLE));
		marker.on(GoogleMapsEvent.MARKER_DRAG_END).subscribe(params => this._polygon.setFillColor(this.COLOR_SHAPE_VISIBLE));
		return marker;
	}

	private adjustMarkersIndexes(): void {
		this._points.forEach((point, index, arr) => point.marker.set('_index', index));
	}

	private nextIndex(): number {
		return this._points.length;
	}

	public isPolygonDrew(): boolean {
		if (this._points.length >= 3)
			return true
		return false;
	}

	private async changeMarkerPosition(marker: Marker): Promise<void> {
		const index = marker.get('_index');
		const latLng = marker.getPosition();
		this._points[index].changePosition(latLng);
		this._polygon.getPoints().setAt(index, latLng);
	}

	private drawPolygon(): void {
		if (this._polygon) {
			this._polygon.remove();
		}
		this._polygon = this.map.addPolygonSync({
			points: this.getLatLngPoints(),
			strokeColor: '#ffffff',
			fillColor: this.COLOR_SHAPE_VISIBLE,
			strokeWidth: 3,
			clickable: true
		});

        this.areaShape = PolygonUtil.calcPolygonAreaHa(this._polygon);
	}

	private observablePolygonArea(action: String) {
		if (action == "start") {
			this._observable = Observable.interval(100)
				.subscribe(p => {
                    this.areaShape = PolygonUtil.calcPolygonAreaHa(this._polygon);
				});
		}
		else if (this._observable) {
			this._observable.unsubscribe();
		}
	}

	public getLatLngPoints(): ILatLng[] {
		return this._points.map(p => p.latLng);
	}

	public getSimplifiedPoints(): ILatLng[] {
		return this.getSimplifiedPointsByList(this.getLatLngPoints());
	}

	public getSimplifiedPointsByList(points: ILatLng[]): ILatLng[] {
        let listPoint = [].concat(points);
        listPoint.push(points[0]);
		const turfCoords = PolygonUtil.coordsGoogleToTurf(listPoint);
		const polygon = turf.polygon([turfCoords]);
		const simplifiedTurfPolygon = turf.simplify(polygon, {
			tolerance: 0.00001, 
			highQuality: false
		});
		return PolygonUtil.coordsTurfToGoogle(simplifiedTurfPolygon.geometry.coordinates[0]);
	}

	private unselectMiddlePoints(): void {
		this._middleMarkers.forEach(m => {
			m.setIcon({ url: './assets/imgs/marker-new.png' });
			m.setDraggable(false);
		})
	}

	public getAreaPolygon(): Number {
		return this.areaShape;
	}

}